import OrgPageTemplate from '@admin/components/organization/OrgPageTemplate';
import useBreadcrumbs from '@admin/hooks/useAdminBreadcrumbs';
import styled from '@emotion/styled';
import * as enabledApi from '@shared/api/enabledPlayApi';
import Button from '@shared/components/core/button/CoreButton';
import PageBannerCard from '@shared/components/core/card/PageBannerCard';
import PageHeader from '@shared/components/core/headers/PageHeader';
import StaggeredReveal from '@shared/components/core/motion/StaggerReveal';
import ActionRow from '@shared/components/core/row/ActionRow';
import SearchField from '@shared/components/core/search/SearchField';
import { ContentLayout } from '@shared/components/core/text/ContentTypography';
import { TextSize300 } from '@shared/components/core/text/Typography';
import LicenseExpiredBannerCard from '@shared/components/license/LicenseExpiredBannerCard';
import SpacedLoader from '@shared/components/loading/SpacedLoader';
import { VOICE_LOCALE_FLAGS } from '@shared/constants/localization/LocalizationConstants';
import { extractRoleProperties } from '@shared/hooks/helpers/OrganizationalHelpers';
import useCurrentOrgDetailsFetching from '@shared/hooks/useCurrentOrgDetailsFetching';
import UserDeviceProfileModel from '@shared/models/deviceProfiles/UserDeviceProfileModel';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

export default function OrgProfilesPage() {
    const { t } = useTranslation();
    const { items } = useBreadcrumbs();
    const navigate = useNavigate();
    const {
        currentOrg,
        isExpiredOrg,
        membership,
        isLoading,
        canEditOrganizationProfiles,
        isManagedOrg,
        canManageBilling,
    } = useCurrentOrgDetailsFetching();
    const [orgProfiles, setOrgProfiles] = useState<UserDeviceProfileModel[]>([]);
    const [loadingProfiles, setLoadingProfiles] = useState<boolean>(true);
    const [filter, setFilter] = useState<string>('');

    const myRole = membership ? extractRoleProperties(membership) : undefined;
    const canManage = myRole?.canEditOrganizationProfiles || myRole?.isAdmin;

    useEffect(() => {
        const loadProfiles = async () => {
            if (!currentOrg?.id) return;
            setLoadingProfiles(true);
            try {
                const result = await enabledApi.getOrganizationProfiles(currentOrg.id);
                if (result?.resultType === 'Ok' && result.data) {
                    setOrgProfiles(result.data);
                }
            } catch (error) {
                console.error('Error loading organization profiles:', error);
            } finally {
                setLoadingProfiles(false);
            }
        };

        if (currentOrg?.id) {
            loadProfiles();
        }
    }, [currentOrg?.id]);

    const filteredProfiles = useMemo(() => {
        if (!filter) return orgProfiles;
        const lowerFilter = filter.toLowerCase();
        return orgProfiles.filter(
            (profile) =>
                profile.name.toLowerCase().includes(lowerFilter) ||
                profile.description?.toLowerCase().includes(lowerFilter)
        );
    }, [orgProfiles, filter]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilter(e.target.value);
    };

    const handleClearSearch = () => {
        setFilter('');
    };

    const handleNavigateToProfile = (profileId: string, preview: boolean) => {
        const destination = preview ? `./preview/${profileId}` : `./edit/${profileId}`;
        navigate(destination);
    };

    const handleCreateProfile = () => {
        navigate('./new');
    };

    if (!currentOrg || isLoading || loadingProfiles) return <SpacedLoader />;
    return (
        <OrgPageTemplate>
            <PageHeader
                title={t('common:organization.profiles.title', { value: currentOrg?.name })}
                description={t('common:organization.profiles.description')}
                items={items}
            />
            {isExpiredOrg ? (
                <LicenseExpiredBannerCard
                    organizationId={currentOrg.id}
                    canManageBilling={canManageBilling}
                    isManagedOrg={isManagedOrg}
                    customBillingUrl={currentOrg.customBillingUrl}
                />
            ) : (
                <>
                    {canManage && (
                        <Button $color="solidAccent" $dashed startIcon="plus" onClick={handleCreateProfile}>
                            {t('common:profile.create')}
                        </Button>
                    )}

                    {orgProfiles.length > 1 && (
                        <SearchField
                            label={t('common:profile.search')}
                            value={filter}
                            onChange={handleSearchChange}
                            onClear={handleClearSearch}
                        />
                    )}

                    <ContentLayout>
                        {filteredProfiles.length === 0 && filter && <p>{t('common:search.noResults')}</p>}
                        {filteredProfiles.length === 0 && !filter && canManage && (
                            <PageBannerCard fullHeight description={t('common:organization.profiles.empty')}>
                                <Button $color="primary" startIcon="plus" onClick={handleCreateProfile}>
                                    {t('common:profile.create')}
                                </Button>
                            </PageBannerCard>
                        )}
                        <StaggeredReveal fullWidth>
                            {filteredProfiles.map((profile) => (
                                <ActionRow
                                    key={profile.id}
                                    startComponent={
                                        profile.defaultLocale && (
                                            <IconSpan>{VOICE_LOCALE_FLAGS[profile.defaultLocale]}</IconSpan>
                                        )
                                    }
                                    title={profile.name}
                                    description={profile.description}
                                    onClick={() => handleNavigateToProfile(profile.id!, !canEditOrganizationProfiles)}
                                    iconSize={24}
                                />
                            ))}
                        </StaggeredReveal>
                    </ContentLayout>
                </>
            )}
        </OrgPageTemplate>
    );
}

const IconSpan = styled.span`
    ${TextSize300}
    line-height: 0;
    display: flex;
    align-items: center;
`;
